import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
import { MissionContainer, MissionTitle, MissionText, MissionButton } from '../components/LandingPageComponents';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from '../components/ProfileImage';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import TeamMemberCard from '../components/TeamMemberCard';
import teamMembers from '../data/teamMembers';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(-30px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const scaleIn = keyframes`
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Add HomeHeader for the top-right corner navigation
const HomeHeader = styled.header`
  position: fixed;
  top: 2.5rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    top: 1.75rem;
    right: 1.5rem;
    gap: 0.8rem;
    display: none; // Hide the header on mobile, it will be replaced by the mobile menu
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-text);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  @media (max-width: 768px) {
    span {
      display: none; // Hide text on mobile
    }

    svg {
      margin-right: 0;
    }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-text);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

// Override MissionContainer styling for this page to improve mobile view
const StyledMissionContainer = styled(MissionContainer)`
  margin: 2rem 0 4rem;
  padding: 3rem;

  @media (max-width: 768px) {
    margin: 1.5rem 0 2.5rem;
    padding: 2rem 1.5rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  }
`;

// Override ContentWrapper to improve spacing on mobile
const ContentWrapper = styled.div`
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem; /* Reduced margin to avoid extra space */
  }
`;

// Override PageContainer to improve mobile padding
const PageContainer = styled.div`
  padding: 3rem 2.5rem;
  padding-top: 7rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  font-family: 'Space Grotesk', sans-serif;
  flex: 1;
  margin-bottom: 0;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    padding-top: 5rem;
    padding-bottom: 0;
    box-sizing: border-box;
  }
`;

const MainContainer = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--brand-canvas);
  position: relative;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-gold) 100%);
  color: white;
  padding: 5rem 4rem;
  border-radius: 20px;
  margin-bottom: 4rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(26, 26, 26, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 3rem 1.25rem;
    margin-bottom: 2.5rem;
    border-radius: 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.25rem;
  text-wrap: balance;
  line-height: 1.2;
  position: relative;
  z-index: 2;
  letter-spacing: -0.02em;
  max-width: 900px;
  text-transform: lowercase;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
    line-height: 1.25;
  }
`;

const HeroTagline = styled.p`
  text-wrap: balance;
  font-size: 1.25rem;
  font-weight: 400;
  max-width: 600px;
  position: relative;
  z-index: 2;
  opacity: 0.85;
  line-height: 1.5;
  text-transform: lowercase;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
  }
`;

const Section = styled.div<{ delay?: number }>`
  background: linear-gradient(145deg, #ffffff 0%, var(--brand-canvas) 100%);
  border-radius: 24px;
  padding: 3rem 3rem 3.5rem;
  box-shadow: 0 8px 32px rgba(26, 26, 26, 0.06),
              0 2px 8px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(198, 167, 94, 0.08);
  transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
  display: flex;
  flex-direction: column;

  @media (max-width: 1199px) {
    padding: 2.75rem 2.5rem 3rem;
    margin-bottom: 0;
    box-shadow: 0 6px 24px rgba(26, 26, 26, 0.05);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.75rem 2.5rem;
    border-radius: 18px;
    margin-bottom: 0;
    box-shadow: 0 4px 16px rgba(26, 26, 26, 0.04);
  }

  &:hover {
    box-shadow: 0 12px 48px rgba(26, 26, 26, 0.12),
                0 4px 12px rgba(198, 167, 94, 0.08);
    transform: translateY(-8px) scale(1.01);
    border-color: rgba(198, 167, 94, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 80px;
    background: linear-gradient(180deg, var(--brand-gold) 0%, var(--brand-black) 100%);
    border-radius: 0 4px 4px 0;
    opacity: 0.9;
    transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: 768px) {
      width: 4px;
      height: 50px;
    }
  }

  &:hover::before {
    height: 120px;
    width: 8px;
    opacity: 1;

    @media (max-width: 768px) {
      height: 70px;
      width: 5px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle at center, rgba(198, 167, 94, 0.03) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
    transition: all 0.6s ease;

    @media (max-width: 768px) {
      width: 100px;
      height: 100px;
    }
  }

  &:hover::after {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle at center, rgba(198, 167, 94, 0.06) 0%, transparent 70%);
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.95rem;
  color: var(--brand-black);
  margin-bottom: 2.25rem;
  font-weight: 800;
  text-wrap: balance;
  display: flex;
  align-items: center;
  line-height: 1.15;
  letter-spacing: -0.03em;
  position: relative;
  padding-bottom: 1rem;
  text-transform: lowercase;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 70px;
    height: 4px;
    background: linear-gradient(90deg, var(--brand-gold) 0%, rgba(198, 167, 94, 0.4) 100%);
    border-radius: 3px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${Section}:hover &::after {
    width: 120px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1.75rem;
    padding-bottom: 0.75rem;

    &::after {
      width: 55px;
      height: 3px;
    }

    ${Section}:hover &::after {
      width: 85px;
    }
  }
`;

const SectionContent = styled.div`
  color: var(--brand-text-secondary);
  line-height: 1.8;
  font-size: 1.1rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  p {
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      margin-bottom: 1rem;
      font-size: 1rem;
      line-height: 1.6;
    }
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const InsightsContainer = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @media (max-width: 768px) {
    gap: 1.75rem;
  }
`;

// Mobile menu components
const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 10px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-charcoal);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;

    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '4px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '14px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4.5rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0.75rem 0;
  z-index: 200000; /* above prompt bars */
  flex-direction: column;
  width: 190px;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.25s ease-out;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  transition: all 0.2s ease;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--brand-text);

    svg {
      opacity: 1;
    }
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--brand-text);

    svg {
      opacity: 1;
    }
  }
`;

const MobileMenuLogoutButton = styled.button`
  color: #ff4d4f;
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(255, 77, 79, 0.05);
    color: #ff1f23;

    svg {
      opacity: 1;
    }
  }
`;

const TeamExpertise: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const location = useLocation();

  const insightsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (insightsRef.current) {
      const items = insightsRef.current.querySelectorAll('.insight-item');
      items.forEach((item, index) => {
        (item as HTMLElement).style.setProperty('--item-index', `${index}`);
      });
    }
  }, []);

  const handleLogin = () => {
    navigate('/auth', { state: { returnUrl: '/team' } });
  };

  const handleJoin = () => {
    if (isAuthenticated) {
      navigate('/desktop');
    } else {
      setIsBookingModalOpen(true);
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) &&
          (event.target as Element).closest('[data-mobile-menu="toggle"]') === null) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <MainContainer>
      <SeoHead
        title="Team | prest0"
        tabTitle="team"
        description="Meet the team behind prest0's enterprise AI employees platform for law and finance—experts in AI, operations, and large-scale platform development."
        keywords="prest0 team, enterprise AI employees, AI platform team, law finance AI, enterprise automation leadership"
        canonicalUrl="https://prest0.ai/team"
        schemaType="WebPage"
      />
      <HomeHeader>
        
        <HeaderIconLink to="/mission">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
            <line x1="4" y1="22" x2="4" y2="15"></line>
          </svg>
          <span>mission</span>
        </HeaderIconLink>
        <HeaderIconLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6" />
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
            <line x1="9" y1="22" x2="15" y2="22" />
          </svg>
          <span>insights</span>
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <span>home</span>
        </HeaderIconLink>
        {isAuthenticated ? (
          <ProfileImage size={36} />
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            <span>login</span>
          </HeaderIconButton>
        )}
        <NotificationsBell />
      </HomeHeader>

      {/* Mobile Menu */}
      <MobileMenuToggle onClick={() => setMenuOpen(!menuOpen)} data-mobile-menu="toggle">
        <MobileMenuIcon isOpen={menuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>

      <MobileMenuDropdown isOpen={menuOpen} ref={mobileMenuRef}>
        
        {!location.pathname.startsWith('/team') && (
          <MobileMenuLink to="/team" onClick={() => setMenuOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            team
          </MobileMenuLink>
        )}
        <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          mission
        </MobileMenuLink>
        <MobileMenuLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6" />
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
            <line x1="9" y1="22" x2="15" y2="22" />
          </svg>
          insights
        </MobileMenuLink>

        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M16 12h.01"/>
                <path d="M2 10h20"/>
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M16 12h.01" />
                  <path d="M2 10h20" />
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            {!location.pathname.startsWith('/profile') && (
              <MobileMenuLink to="/profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                profile
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              settings
            </MobileMenuLink>
            <MobileMenuLogoutButton onClick={() => {
              setMenuOpen(false);
              logout();
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileMenuLogoutButton>
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
          </>
        ) : (
          <MobileMenuButton onClick={() => {
            setMenuOpen(false);
            handleLogin();
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

      <PageContainer>
        <ContentWrapper>
          {/* Hero */}
          <HeroSection>
            <HeroTitle>meet the team</HeroTitle>
            <HeroTagline>the people building AI employees for law and finance</HeroTagline>
          </HeroSection>

          {/* Team grid */}
          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamMemberCard
                key={member.id}
                member={member}
                delay={0.1 + index * 0.1}
              />
            ))}
          </TeamGrid>

          {/* Insights */}
          <Section delay={0.5} style={{ marginBottom: '4rem' }}>
            <SectionTitle>insights</SectionTitle>
            <SectionContent>
              <InsightsContainer>
                <InsightsList ref={insightsRef}>
                  <InsightItem className="insight-item">
                    <InsightNumber>01</InsightNumber>
                    <InsightContent>
                      <InsightHeading>reasoning is now affordable</InsightHeading>
                      <InsightText>
                        for the first time, advanced reasoning engines operate at scale without prohibitive cost. agents aren't bound to narrow tasks — they can think, adapt, and tackle problems once ignored.
                      </InsightText>
                    </InsightContent>
                  </InsightItem>

                  <InsightItem className="insight-item">
                    <InsightNumber>02</InsightNumber>
                    <InsightContent>
                      <InsightHeading>learning is the advantage</InsightHeading>
                      <InsightText>
                        effective agents improve with every cycle. by absorbing streams of data and refining continuously, they compound intelligence over time — becoming sharper the longer they run.
                      </InsightText>
                    </InsightContent>
                  </InsightItem>

                  <InsightItem className="insight-item">
                    <InsightNumber>03</InsightNumber>
                    <InsightContent>
                      <InsightHeading>a new kind of startup</InsightHeading>
                      <InsightText>
                        a new generation of startups is emerging, built around agents from day one. with reasoning and learning at their core, they move faster, adapt quicker, and unlock opportunities traditional models can't reach.
                      </InsightText>
                    </InsightContent>
                  </InsightItem>
                </InsightsList>
              </InsightsContainer>
            </SectionContent>
          </Section>

          {/* Mission CTA */}
          <div style={{ marginBottom: '4rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <StyledMissionContainer>
              <MissionTitle>
                <span>the</span> mission
              </MissionTitle>
              <MissionText>
                creating a future where the value of ai agents benefits everyone
              </MissionText>
              <MissionButton onClick={() => {
                navigate('/mission');
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                  <line x1="4" y1="22" x2="4" y2="15"></line>
                </svg>
                the mission
              </MissionButton>
            </StyledMissionContainer>
          </div>
        </ContentWrapper>
      </PageContainer>

      <GlobalFooter />
    </MainContainer>
  );
};

const InsightItem = styled.div`
  display: flex;
  gap: 1.5rem;
  animation: ${slideIn} 0.5s ease-out forwards;
  opacity: 0;
  animation-delay: calc(0.2s * var(--item-index, 0));

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding-bottom: 1.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }
`;

const InsightNumber = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  color: rgba(198, 167, 94, 0.2);
  line-height: 1;
  min-width: 60px;
  text-align: center;
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.1) 0%, rgba(198, 167, 94, 0.1) 100%);
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    height: 50px;
    width: 50px;
    min-width: 50px;
  }
`;

const InsightContent = styled.div`
  flex: 1;
`;

const InsightHeading = styled.h4`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--brand-black);

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const InsightText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export default TeamExpertise;
